import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  BookOpen,
  Edit3,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trophy,
  ChevronLeft,
  ChevronRight,
  Eye,
  List,
  AlertTriangle,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/bookBasedComprehensionGame.css";
import { API_BASE, DEV_MODE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";

const MODE_CFG = gameConfig.bookComprehension;

const CACHE_KEY = (bookId, chapterId) => `book_comp_${bookId}_${chapterId}`;

const getCached = (bookId, chapterId) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY(bookId, chapterId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const setCached = (bookId, chapterId, data) => {
  try {
    localStorage.setItem(CACHE_KEY(bookId, chapterId), JSON.stringify(data));
  } catch {}
};
const clearCached = (bookId, chapterId) => {
  localStorage.removeItem(CACHE_KEY(bookId, chapterId));
};

export default function BookBasedComprehensionGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    bookId,
    chapterId,
    bookTitle,
    chapterDesc,
    questionType = "mcq",
  } = location.state || {};

  const [gameState, setGameState] = useState("loading");
  const [passage, setPassage] = useState("");
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [writtenQuestions, setWrittenQuestions] = useState([]);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [writtenAnswers, setWrittenAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markingResults, setMarkingResults] = useState(null);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const [currentReviewQ, setCurrentReviewQ] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const hasPostedRef = React.useRef(false);
  const hasInitRef = React.useRef(false);
  const MODE_KEY = `book-${questionType}`;

  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeGame();
  }, []);

  useEffect(() => {
    if (DEV_MODE) return;
    if (gameState === "playing") {
      const timer = setTimeout(() => setTimeElapsed((t) => t + 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeElapsed, gameState]);

  const initializeGame = async () => {
    setLoading(true);
    setError("");
    try {
      let data = getCached(bookId, chapterId);

      if (!data) {
        const response = await apiFetch(
          `/api/generate-book-comprehension`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookId, chapterId }),
          },
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        data = await response.json();
        if (!data.mcqQuestions || !data.writtenQuestions) {
          throw new Error("Invalid data received from server");
        }
        setCached(bookId, chapterId, data);
      }

      setPassage(data.passage || "");
      setMcqQuestions(data.mcqQuestions);
      setWrittenQuestions(data.writtenQuestions);

      const initMcq = {};
      data.mcqQuestions.forEach((_, i) => {
        initMcq[i] = null;
      });
      setMcqAnswers(initMcq);

      const initWritten = {};
      data.writtenQuestions.forEach((_, i) => {
        initWritten[i] = "";
      });
      setWrittenAnswers(initWritten);

      setGameState("playing");
      setTimeElapsed(0);
    } catch (err) {
      console.error("Error initializing book game:", err);
      navigate("/error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (isExplicit = false) => {
    setLoading(true);
    setGameState("reviewing");
    try {
      let results;

      if (questionType === "mcq") {
        // Auto-mark MCQ only
        const mcqResults = mcqQuestions.map((q, i) => ({
          questionIndex: i,
          correct: mcqAnswers[i] === q.correct,
          selectedAnswer: mcqAnswers[i],
          correctAnswer: q.correct,
          explanation: `The correct answer is ${q.correct}: ${q.options?.[q.correct] || ""}`,
        }));
        const mcqCorrect = mcqResults.filter((r) => r.correct).length;
        results = {
          type: "mcq",
          mcqResults,
          mcqCorrect,
          totalScore: mcqCorrect,
          maxScore: mcqQuestions.length,
        };
      } else {
        // AI-mark written only
        const writtenResponse = await apiFetch(
          `/api/mark-comprehension`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              questions: writtenQuestions,
              userAnswers: writtenAnswers,
            }),
          },
        );
        if (!writtenResponse.ok)
          throw new Error("Failed to mark written answers");
        const writtenMarking = await writtenResponse.json();

        const maxWritten = writtenQuestions.reduce((s, q) => s + q.marks, 0);
        results = {
          type: "written",
          writtenResults: writtenMarking.results || [],
          totalScore: writtenMarking.totalScore ?? 0,
          maxScore: writtenMarking.maxScore ?? maxWritten,
        };
      }

      setMarkingResults(results);

      const coins = calculateCoins(
        results.totalScore,
        results.maxScore,
      );
      setCoinsEarned(coins);

      if (isExplicit) {
        await addCoinsAndXP(coins);
        await saveSession(results.totalScore);
        clearCached(bookId, chapterId);
      }

      const userId = parseInt(localStorage.getItem("userId"), 10);
      if (userId) {
        const questions = results.type === "mcq" ? mcqQuestions : writtenQuestions;
        const userAnswers = results.type === "mcq" ? mcqAnswers : writtenAnswers;
        apiFetch(`/api/results/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            gameType: "book_comprehension",
            title: `${bookTitle} — ${chapterId}`,
            score: results.totalScore ?? 0,
            maxScore: results.maxScore ?? questions.length,
            resultJson: { bookTitle, chapterId, type: results.type, questions, userAnswers, markingResults: results },
          }),
        }).catch(() => {});
      }

      setGameState("finished");
    } catch (err) {
      console.error("Error submitting:", err);
      setError("Failed to mark answers. Please try again.");
      setGameState("playing");
    } finally {
      setLoading(false);
    }
  };

  const calculateCoins = (score, _maxScore) => {
    return Math.floor(score * MODE_CFG.baseCoinsPerMark);
  };

  const addCoinsAndXP = async (coins) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || coins <= 0) return;
    try {
      await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coins }),
      });
      await apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coins * MODE_CFG.xpMultiplier }),
      });
    } catch (err) {
      console.error("Error updating coins/XP:", err);
    }
  };

  const saveSession = async (score) => {
    if (hasPostedRef.current) return;
    hasPostedRef.current = true;
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || isNaN(userId)) return;
    try {
      await apiFetch(`/api/sessions/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, mode: MODE_KEY, score }),
        keepalive: true,
      });
    } catch (err) {
      console.error("Error saving session:", err);
    }
  };

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const activeQuestions =
    questionType === "mcq" ? mcqQuestions : writtenQuestions;

  // ── Loading screen ──
  if (gameState === "loading") {
    return (
      <div className="bbc-loading">
        <BookOpen className={loading ? "bbc-spin" : ""} size={48} />
        <p>
          {loading
            ? "Preparing your book comprehension challenge..."
            : "Preparing..."}
        </p>
        {error && (
          <div className="bbc-error-msg">
            <AlertCircle size={16} /> {error}
          </div>
        )}
      </div>
    );
  }

  // ── Reviewing screen ──
  if (gameState === "reviewing") {
    return (
      <div className="bbc-loading">
        {questionType === "written" ? (
          <Edit3 className="bbc-spin" size={48} />
        ) : (
          <CheckCircle className="bbc-spin" size={48} />
        )}
        <p>
          {questionType === "written"
            ? "AI is marking your written answers..."
            : "Calculating your score..."}
        </p>
        {questionType === "written" && (
          <p className="bbc-sub">This may take a moment</p>
        )}
      </div>
    );
  }

  // ── Finished screen ──
  if (gameState === "finished") {
    if (reviewMode) {
      const reviewQuestions =
        questionType === "mcq" ? mcqQuestions : writtenQuestions;
      const reviewResults =
        questionType === "mcq"
          ? markingResults?.mcqResults || []
          : markingResults?.writtenResults || [];

      return (
        <div className="bbc-review-screen">
          <div className="bbc-review-header">
            <button
              className="bbc-review-back"
              onClick={() => setReviewMode(false)}
            >
              <ArrowLeft size={14} /> Back to Results
            </button>
            <div className="bbc-review-title-row">
              <h2>Answer Review — {bookTitle}</h2>
              <span className={`bbc-type-badge ${questionType}`}>
                {questionType === "mcq" ? (
                  <List size={12} />
                ) : (
                  <Edit3 size={12} />
                )}
                {questionType === "mcq" ? "Multiple Choice" : "Written Answers"}
              </span>
            </div>
          </div>

          <div className="bbc-review-content">
            <div className="bbc-review-passage">
              <h3>Reading Passage</h3>
              <div className="bbc-passage-text">{passage}</div>
            </div>

            <div className="bbc-review-questions">
              <div className="bbc-review-q-nav">
                {reviewQuestions.map((_, i) => (
                  <button
                    key={i}
                    className={`bbc-review-dot ${i === currentReviewQ ? "active" : ""}`}
                    onClick={() => setCurrentReviewQ(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <div className="bbc-review-q-panel">
                <h4>Question {currentReviewQ + 1}</h4>
                <div className="bbc-review-q-text">
                  {reviewQuestions[currentReviewQ]?.question}
                </div>

                {questionType === "mcq" ? (
                  <div className="bbc-review-mcq">
                    {Object.entries(
                      reviewQuestions[currentReviewQ]?.options || {},
                    ).map(([key, text]) => {
                      const isCorrect =
                        key === reviewQuestions[currentReviewQ]?.correct;
                      const isSelected = mcqAnswers[currentReviewQ] === key;
                      return (
                        <div
                          key={key}
                          className={`bbc-review-option ${isCorrect ? "correct" : ""} ${
                            isSelected && !isCorrect ? "wrong" : ""
                          }`}
                        >
                          <span className="bbc-option-key">{key}</span>
                          <span>{text}</span>
                          {isCorrect && (
                            <CheckCircle size={14} className="bbc-check" />
                          )}
                        </div>
                      );
                    })}
                    <p className="bbc-review-explanation">
                      {reviewResults[currentReviewQ]?.explanation}
                    </p>
                  </div>
                ) : (
                  <div className="bbc-review-written">
                    <div className="bbc-review-answer-box">
                      <h5>Your Answer:</h5>
                      <p>
                        {writtenAnswers[currentReviewQ] || "No answer provided"}
                      </p>
                    </div>
                    <div className="bbc-review-marks">
                      Marks:{" "}
                      {reviewResults[currentReviewQ]?.marksAwarded ?? "—"} /{" "}
                      {reviewQuestions[currentReviewQ]?.marks}
                    </div>
                    <div className="bbc-review-feedback">
                      <h5>Explanation:</h5>
                      <p>{reviewResults[currentReviewQ]?.explanation}</p>
                      <h5>Feedback:</h5>
                      <p>{reviewResults[currentReviewQ]?.feedback}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bbc-review-nav-controls">
                <button
                  className="bbc-nav-btn"
                  onClick={() => setCurrentReviewQ((q) => Math.max(0, q - 1))}
                  disabled={currentReviewQ === 0}
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <button
                  className="bbc-nav-btn"
                  onClick={() =>
                    setCurrentReviewQ((q) =>
                      Math.min(reviewQuestions.length - 1, q + 1),
                    )
                  }
                  disabled={currentReviewQ === reviewQuestions.length - 1}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bbc-results">
        <div className="bbc-results-content">
          <Trophy className="bbc-trophy" size={56} />
          <h2>Challenge Complete!</h2>
          <div className="bbc-results-book-info">
            <span className="bbc-results-book-title">{bookTitle}</span>
            <span className="bbc-results-chapter-desc">{chapterDesc}</span>
            <span className={`bbc-type-badge ${questionType}`}>
              {questionType === "mcq" ? (
                <List size={12} />
              ) : (
                <Edit3 size={12} />
              )}
              {questionType === "mcq" ? "Multiple Choice" : "Written Answers"}
            </span>
          </div>

          <div className="bbc-score-breakdown">
            <div className="bbc-score-row">
              <span>
                {questionType === "mcq" ? "Correct Answers" : "Written Score"}
              </span>
              <strong>
                {questionType === "mcq"
                  ? `${markingResults.mcqCorrect} / ${mcqQuestions.length}`
                  : `${markingResults.totalScore} / ${markingResults.maxScore}`}
              </strong>
            </div>
            <div className="bbc-score-row total">
              <span>Total Score</span>
              <strong>
                {markingResults.totalScore} / {markingResults.maxScore}
              </strong>
            </div>
            <div className="bbc-score-row coins">
              <span>
                <Star size={14} /> Coins Earned
              </span>
              <strong>{coinsEarned}</strong>
            </div>
          </div>

          <div className="bbc-action-btns">
            <button
              className="bbc-primary-btn"
              onClick={() => setReviewMode(true)}
            >
              <Eye size={16} /> Review Answers
            </button>
            <button
              className="bbc-secondary-btn"
              onClick={() => navigate("/games/books")}
            >
              <ArrowLeft size={16} /> Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Playing screen ──
  return (
    <div className="bbc-game">
      <header className="bbc-header">
        <div className="bbc-header-left">
          <div className="bbc-header-book">
            <span className="bbc-book-title">{bookTitle}</span>
            <span className="bbc-chapter-label">{chapterDesc}</span>
          </div>
        </div>
        <div className="bbc-header-right">
          <span className={`bbc-type-pill ${questionType}`}>
            {questionType === "mcq" ? <List size={12} /> : <Edit3 size={12} />}
            {questionType === "mcq" ? "Multiple Choice" : "Written Answers"}
          </span>
          <span className="bbc-q-progress">
            Q {currentQ + 1} / {activeQuestions.length}
          </span>
          <span className="bbc-timer">
            <Clock size={14} /> {formatTime(timeElapsed)}
          </span>
          <button
            className="bbc-back-btn"
            onClick={() => navigate("/games/books")}
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </header>

      {error && (
        <div className="bbc-error-banner">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="bbc-content">
        <div className="bbc-passage-panel">
          <h3 className="bbc-passage-title">
            <BookOpen size={16} /> Reading Passage
          </h3>
          <div className="bbc-passage-text">{passage}</div>
        </div>

        <div className="bbc-questions-panel">
          <div className="bbc-q-dots">
            {activeQuestions.map((_, i) => {
              const answered =
                questionType === "mcq"
                  ? mcqAnswers[i] !== null
                  : writtenAnswers[i]?.trim().length > 0;
              return (
                <button
                  key={i}
                  className={`bbc-q-dot ${i === currentQ ? "active" : ""} ${answered ? "answered" : ""}`}
                  onClick={() => setCurrentQ(i)}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {questionType === "mcq" ? (
            <McqQuestion
              question={mcqQuestions[currentQ]}
              questionIndex={currentQ}
              selectedAnswer={mcqAnswers[currentQ]}
              onSelect={(ans) =>
                setMcqAnswers((prev) => ({ ...prev, [currentQ]: ans }))
              }
            />
          ) : (
            <WrittenQuestion
              question={writtenQuestions[currentQ]}
              questionIndex={currentQ}
              answer={writtenAnswers[currentQ] || ""}
              onAnswerChange={(ans) =>
                setWrittenAnswers((prev) => ({ ...prev, [currentQ]: ans }))
              }
            />
          )}

          <div className="bbc-nav-controls">
            <button
              className="bbc-nav-btn"
              onClick={() => setCurrentQ((q) => Math.max(0, q - 1))}
              disabled={currentQ === 0}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              className="bbc-submit-btn"
              onClick={() => {
                const allAnswered = questionType === "mcq"
                  ? activeQuestions.every((_, i) => mcqAnswers[i] !== undefined && mcqAnswers[i] !== null)
                  : activeQuestions.every((_, i) => writtenAnswers[i]?.trim().length > 0);
                if (allAnswered) {
                  handleSubmit(true);
                } else {
                  setShowSubmitModal(true);
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <RefreshCw size={14} className="bbc-spin" /> Submitting...
                </>
              ) : (
                "Finish & Submit"
              )}
            </button>
            <button
              className="bbc-nav-btn"
              onClick={() =>
                setCurrentQ((q) => Math.min(activeQuestions.length - 1, q + 1))
              }
              disabled={currentQ === activeQuestions.length - 1}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="submit-confirm-overlay">
          <div className="submit-confirm-modal">
            <div className="submit-confirm-icon">
              <AlertTriangle size={26} />
            </div>
            <h3>Unanswered Questions</h3>
            <p>
              You have{" "}
              {questionType === "mcq"
                ? activeQuestions.filter((_, i) => mcqAnswers[i] === undefined || mcqAnswers[i] === null).length
                : activeQuestions.filter((_, i) => !writtenAnswers[i]?.trim().length).length}{" "}
              unanswered question(s). Are you sure you want to submit?
            </p>
            <div className="submit-confirm-actions">
              <button className="submit-confirm-back" onClick={() => setShowSubmitModal(false)}>
                Go Back
              </button>
              <button className="submit-confirm-go" onClick={() => { setShowSubmitModal(false); handleSubmit(true); }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function McqQuestion({ question, questionIndex, selectedAnswer, onSelect }) {
  if (!question) return null;
  return (
    <div className="bbc-question-panel">
      <div className="bbc-q-header">
        <h4>Question {questionIndex + 1}</h4>
        <span className="bbc-q-marks">[1 mark]</span>
      </div>
      <div className="bbc-q-text">{question.question}</div>
      <div className="bbc-mcq-options">
        {Object.entries(question.options || {}).map(([key, text]) => (
          <button
            key={key}
            className={`bbc-option ${selectedAnswer === key ? "selected" : ""}`}
            onClick={() => onSelect(key)}
          >
            <span className="bbc-option-key">{key}</span>
            <span className="bbc-option-text">{text}</span>
            {selectedAnswer === key && (
              <CheckCircle size={14} className="bbc-selected-icon" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function WrittenQuestion({ question, questionIndex, answer, onAnswerChange }) {
  if (!question) return null;
  return (
    <div className="bbc-question-panel">
      <div className="bbc-q-header">
        <h4>Question {questionIndex + 1}</h4>
        <span className="bbc-q-marks">[{question.marks} marks]</span>
      </div>
      <div className="bbc-q-text">{question.question}</div>
      <textarea
        className="bbc-answer-input"
        value={answer}
        onChange={(e) => onAnswerChange(e.target.value)}
        placeholder="Type your answer here..."
        rows={question.marks >= 4 ? 9 : question.marks >= 3 ? 7 : 5}
      />
      <div className="bbc-char-count">{answer.length} characters</div>
    </div>
  );
}
